import React from 'react';
import type { ExtraConfig } from '@gov-nx/core/app';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import {
	ProfilROBReklamaceContextProvider,
	useProfilROBContextInstance,
} from '@gov-nx/module/page';
import {
	Button,
	ContentLayout,
	Dialog,
	ErrorStatusBanner,
	Icon,
	LayoutGap,
} from '@gov-nx/ui/web';
import { ChangePhotoView } from './ChangePhotoView';
import { ProfilROBSkeleton } from './ProfilROBSkeleton';
import { ProfileContentLayout } from './ProfileContentLayout';
import { ProfileROBComplaintSubmitButton } from './ProfileROBComplaintSubmitButton';
import { ProfileROBComplaintView } from './ProfileROBComplaintView';
import { ProfileROBDataView } from './ProfileROBDataView';

type ProfileRobViewConfig =
	ExtraConfig['profileRoutes']['robConfig']['profileRobView'];

export interface RegistrObyvatelFormProps extends ProfileRobViewConfig {
	navigation: React.ReactNode;
	code: PageCode;
}

const formId = 'complain-form-rob';

export const ProfileROBView = ({
	navigation,
	code,
	...ProfileROBViewConfig
}: RegistrObyvatelFormProps) => {
	const { data, photo, controls, setControls } = useProfilROBContextInstance();
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const ts = getLocalizeCurried(code);

	if (controls.initialLoading) {
		return (
			<ContentLayout customClasses="pt-4!">
				{navigation}
				<ProfilROBSkeleton />
			</ContentLayout>
		);
	}

	if (controls.initialDataError) {
		return (
			<ContentLayout customClasses="pt-4!">
				{navigation}
				<ErrorStatusBanner code={code} />
			</ContentLayout>
		);
	}

	return (
		<LayoutGap>
			<ProfileContentLayout {...ProfileROBViewConfig.profileContentLayout}>
				{navigation}
				<section className="flex flex-col gap-(--templates-margin-xl) mt-(--templates-margin-l)! [ sm:flex sm:flex-row sm:items-start ]">
					{photo ? (
						<div className="relative w-[100px] shrink-0">
							<img
								src={`data:image/png;base64, ${photo}`}
								alt=""
								width={100}
								height={100}
								className="rounded-lg border border-(--border-subtlest)"
							/>
							<Button
								onClick={() => setControls({ displayPhoto: true })}
								color="primary"
								aria-label={ts('akce.neni-vase-foto-aktualni')}
								type="base"
								size="s"
								className="button-edit-photo">
								<Icon
									name="pencil-square"
									type="templates"
								/>
							</Button>
						</div>
					) : null}
					<div className="w-full mt-0!">
						<LayoutGap gap="l">
							<ProfileROBDataView
								code={code}
								data={data}
							/>
							<Button
								onClick={() => setControls({ displayComplaint: true })}
								nativeType="submit"
								color="primary"
								size="m"
								type="outlined"
								isExpandedMobile
								aria-label={ts('akce.podnet-k-oprave-udaju')}>
								{ts('akce.podnet-k-oprave-udaju')}
							</Button>
						</LayoutGap>
					</div>
				</section>
				<Dialog
					isOpen={controls.displayPhoto}
					onClose={() => setControls({ displayPhoto: false })}
					title={<h3>{ts('foto.moje-fotografie')}</h3>}
					accessibleCloseLabel={ts('akce.wcag.zavrit-fotografii')}>
					<ChangePhotoView
						photo={photo}
						code={code}
					/>
				</Dialog>
				{controls.displayComplaint && (
					<ProfilROBReklamaceContextProvider
						code={code}
						data={data}
						onComplaintSubmit={() => setControls({ displayComplaint: false })}>
						<Dialog
							isOpen={controls.displayComplaint}
							onClose={() => setControls({ displayComplaint: false })}
							title={<h3>{ts('formular.podnet-k-oprave-udaju')}</h3>}
							footer={<ProfileROBComplaintSubmitButton formId={formId} />}>
							<ProfileROBComplaintView formId={formId} />
						</Dialog>
					</ProfilROBReklamaceContextProvider>
				)}
			</ProfileContentLayout>
		</LayoutGap>
	);
};
